import { Box, Button, Stack, Typography } from '@locus-taxy/disco-ui';
import { useTranslation } from '@locus-taxy/encore-i18n-toolkit';
import { getTestId } from '@locus-taxy/encore-utils';
import { TestIdAction } from '../utils/testIdUtils';
import { useProfilePage } from './UserDetails/useProfilePage';
import { ProfileHeaderCard } from './UserDetails/ProfileHeaderCard';
import { PersonalInfoSection } from './UserDetails/PersonalInfoSection';
import { LocaleSettingsSection } from './UserDetails/LocaleSettingsSection';
import { WorkProfileSection } from './UserDetails/WorkProfileSection';
import { SessionAccessSection } from './UserDetails/SessionAccessSection';
import { UnsavedChangesDialog } from './UserDetails/UnsavedChangesDialog';

export default function UserDetails() {
  const { t } = useTranslation();
  const {
    mode,
    isDirty,
    isSaving,
    showUnsavedDialog,
    canUpdate,
    clientId,
    fullName,
    entityData,
    configuredTimezone,
    configuredLocale,
    timezoneOptions,
    localeOptions,
    lastUpdated,
    photoUrl,
    photoLoading,
    hasPhoto,
    workProfileData,
    personalInfoViewFields,
    localeViewFields,
    personalInfoFormRef,
    localeFormRef,
    handleEditClick,
    handleCancel,
    handleSave,
    handleDialogStay,
    handleDialogLeave,
    handlePersonalInfoChange,
    handleLocaleChange,
    handlePhotoSelect,
    handlePhotoRemove,
    handleLogout,
  } = useProfilePage();

  return (
    <Stack
      direction="column"
      className="h-full overflow-hidden"
      data-testid={getTestId({
        action: TestIdAction.VIEW,
        object: 'account-page-container',
        page: 'account',
      })}
    >
      {/* TitleBar */}
      <Stack
        direction="row"
        justify="between"
        align="center"
        className="shrink-0 bg-card px-12 py-6"
        data-testid={getTestId({
          action: TestIdAction.VIEW,
          object: 'profile-title-bar',
          page: 'account',
        })}
      >
        <Stack direction="column" gap={1}>
          <Typography variant="h4" fontWeight="bold">
            {t('dashboard.userDetails.profileSettings', 'Profile Settings')}
          </Typography>
          {lastUpdated && (
            <Typography variant="caption" color="muted">
              {t(
                'dashboard.userDetails.lastUpdated',
                'Last updated: {{date}}',
                {
                  date: lastUpdated,
                }
              )}
            </Typography>
          )}
        </Stack>

        {mode === 'view' ? (
          <Button
            size="lg"
            disabled={!canUpdate}
            onClick={handleEditClick}
            data-testid={getTestId({
              action: TestIdAction.VIEW,
              object: 'edit-profile-button',
              page: 'account',
            })}
          >
            {t('dashboard.userDetails.editProfile', 'Edit Profile')}
          </Button>
        ) : (
          <Stack direction="row" gap={2}>
            <Button
              variant="outline"
              size="lg"
              disabled={isSaving}
              onClick={handleCancel}
              data-testid={getTestId({
                action: TestIdAction.VIEW,
                object: 'cancel-button',
                page: 'account',
              })}
            >
              {t('dashboard.userDetails.cancel', 'Cancel')}
            </Button>
            <Button
              size="lg"
              disabled={isSaving || !isDirty}
              onClick={handleSave}
              data-testid={getTestId({
                action: TestIdAction.VIEW,
                object: 'save-changes-button',
                page: 'account',
              })}
            >
              {t('dashboard.userDetails.saveChanges', 'Save Changes')}
            </Button>
          </Stack>
        )}
      </Stack>

      {/* Scrollable content */}
      <Box className="flex-1 overflow-auto bg-muted/30 px-0 pt-8 pb-12">
        <Box className="mx-auto w-full max-w-3xl px-4">
          <Stack direction="column" gap={3}>
            <ProfileHeaderCard
              mode={mode}
              fullName={fullName}
              designation={
                entityData?.profileInformation?.emailAddress as
                  | string
                  | undefined
              }
              roleName={workProfileData.roleName || undefined}
              photoUrl={photoUrl}
              photoLoading={photoLoading}
              onPhotoSelect={handlePhotoSelect}
              onPhotoRemove={handlePhotoRemove}
              hasPhoto={hasPhoto}
            />
            <PersonalInfoSection
              mode={mode}
              entityData={entityData as Record<string, unknown> | undefined}
              clientId={clientId}
              formRef={personalInfoFormRef}
              onChange={handlePersonalInfoChange}
              viewFields={personalInfoViewFields}
            />
            <LocaleSettingsSection
              mode={mode}
              clientId={clientId}
              formRef={localeFormRef}
              onChange={handleLocaleChange}
              timezone={configuredTimezone}
              timezoneOptions={timezoneOptions}
              locale={configuredLocale}
              localeOptions={localeOptions}
              viewFields={localeViewFields}
            />
            <WorkProfileSection
              roleName={workProfileData.roleName}
              accessDimensionLabel={workProfileData.accessDimensionLabel}
              accessDimensionNames={workProfileData.accessDimensionNames}
              skillNames={workProfileData.skillNames}
              joiningDate={workProfileData.joiningDate}
              locale={configuredLocale}
            />
            <SessionAccessSection onLogout={handleLogout} />
          </Stack>
        </Box>
      </Box>

      <UnsavedChangesDialog
        open={showUnsavedDialog}
        onStay={handleDialogStay}
        onLeave={handleDialogLeave}
      />
    </Stack>
  );
}
